import { gsap } from "gsap";
import { useLayoutEffect, useRef } from "react";

import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import { cn } from "@/shared/lib/utils";

import { MOTION_DURATION, MOTION_EASE } from "./motion.tokens";

export interface ColocaSuccessMarkProps {
  className?: string;
  /** Accessible text; omit when a heading next to it already says it. */
  label?: string;
}

/**
 * A ring and a check that draw themselves, then land with a small overshoot: the one loud moment of a flow
 * that ends well. Drawn fully by default, so without motion it is simply a check.
 */
export function ColocaSuccessMark({ className, label }: ColocaSuccessMarkProps) {
  const ref = useRef<SVGSVGElement>(null);
  const reduced = usePrefersReducedMotion();

  useLayoutEffect(() => {
    const svg = ref.current;
    if (!svg || reduced) return;
    const ctx = gsap.context(() => {
      const ring = svg.querySelector<SVGCircleElement>("[data-ring]");
      const check = svg.querySelector<SVGPathElement>("[data-check]");
      if (!ring || !check) return;
      const ringLength = ring.getTotalLength();
      const checkLength = check.getTotalLength();
      gsap
        .timeline()
        .fromTo(
          svg,
          { scale: 0.6, opacity: 0 },
          { scale: 1, opacity: 1, duration: MOTION_DURATION.slow, ease: MOTION_EASE.pop },
        )
        .fromTo(
          ring,
          { strokeDasharray: ringLength, strokeDashoffset: ringLength },
          { strokeDashoffset: 0, duration: MOTION_DURATION.enter, ease: MOTION_EASE.enter },
          0,
        )
        .fromTo(
          check,
          { strokeDasharray: checkLength, strokeDashoffset: checkLength },
          { strokeDashoffset: 0, duration: MOTION_DURATION.enter * 0.6, ease: MOTION_EASE.enter },
          MOTION_DURATION.enter * 0.6,
        );
    }, svg);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <svg
      ref={ref}
      viewBox="0 0 56 56"
      className={cn("size-14 text-success-container-foreground", className)}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <circle cx="28" cy="28" r="26" className="fill-success-container" />
      <circle
        data-ring
        cx="28"
        cy="28"
        r="25"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        transform="rotate(-90 28 28)"
      />
      <path
        data-check
        d="M18 29 L25 36 L39 21"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
